import { FunctionComponent } from 'react'
import styled from 'styled-components'
import PricingIcon from './PricingIcon'
import { pricingContentWidth } from './PlanCard'
import { enterprise } from '../pricingV2Content'

interface IEnterpriseSectionProps {
    onBookCall: () => void
}

// Mirrors the Enterprise block from the pricing design / leaddelta.com/pricing
const EnterpriseSection: FunctionComponent<IEnterpriseSectionProps> = ({ onBookCall }) => (
    <Container aria-labelledby="enterprise-heading">
        <GlowTopRight aria-hidden="true" />
        <GlowLeft aria-hidden="true" />
        <GlowCenter aria-hidden="true" />
        <Layout>
            <LeftColumn>
                <HighlightCard>
                    <Title id="enterprise-heading">{enterprise.title}</Title>
                    <Description>{enterprise.description}</Description>
                    <BookCallButton type="button" onClick={onBookCall}>Book a call<PricingIcon type="arrowRight" /></BookCallButton>
                </HighlightCard>
                <Quote>
                    <QuoteText>{enterprise.quote}</QuoteText>
                    <QuoteAuthor>{enterprise.quoteAuthor}</QuoteAuthor>
                </Quote>
            </LeftColumn>
            <FeatureGrid>
                {enterprise.features.map(({ icon, title, description }) => (
                    <Feature key={title}>
                        <FeatureHeading>
                            <IconBox><PricingIcon type={icon} /></IconBox>
                            <FeatureTitle>{title}</FeatureTitle>
                        </FeatureHeading>
                        <FeatureDescription>{description}</FeatureDescription>
                    </Feature>
                ))}
            </FeatureGrid>
        </Layout>
    </Container>
)

const Container = styled.section`
    position: relative;
    isolation: isolate;
    overflow: hidden;
    ${pricingContentWidth}
    padding: 16px;
    border-radius: 24px;
    border: 1px solid rgba(12, 17, 31, 0.1);
    background: linear-gradient(158deg, #0C111F 0%, #1D2035 100%);
    font-family: NunitoSans;

    @media only screen and (min-width: 600px) {
        padding: 24px;
    }
    @media only screen and (min-width: 1280px) {
        padding: 28px;
    }
`
const Glow = styled.div`
    position: absolute;
    z-index: -1;
    border-radius: 50%;
    pointer-events: none;
`
const GlowTopRight = styled(Glow)`
    top: -80px;
    right: -80px;
    width: 288px;
    height: 288px;
    background: rgba(81, 76, 227, 0.3);
    filter: blur(32px);
`
const GlowLeft = styled(Glow)`
    top: 96px;
    left: -96px;
    width: 480px;
    height: 480px;
    background: radial-gradient(50% 50% at 50% 50%, rgba(67, 72, 214, 0.3), transparent 70%);
    filter: blur(80px);
`
const GlowCenter = styled(Glow)`
    top: 80px;
    left: 50%;
    width: 400px;
    height: 400px;
    background: radial-gradient(50% 50% at 50% 50%, rgba(107, 87, 227, 0.18), transparent 70%);
    filter: blur(70px);
`
const Layout = styled.div`
    display: grid;
    gap: 20px;

    @media only screen and (min-width: 1280px) {
        grid-template-columns: minmax(0, 1fr) minmax(0, 1.3fr);
        column-gap: 48px;
    }
`
const LeftColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`
const HighlightCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 22px;
    border-radius: 16px;
    background: linear-gradient(to bottom, #665DFF, #453AC6);
    box-shadow: 0 14px 20px 0 rgba(42, 34, 128, 0.45);
`
const Title = styled.h2`
    margin: 0;
    font-family: NunitoSans;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
    color: #FFFFFF;
`
const Description = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    color: rgba(255, 255, 255, 0.75);
`
const BookCallButton = styled.button`
    margin-top: 8px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 16px;
    border: none;
    border-radius: 8px;
    background: #FFFFFF;
    color: #0C111F;
    font-family: NunitoSans;
    font-size: 13px;
    font-weight: 700;
    line-height: 18px;
    box-shadow: 0 4px 6px 0 rgba(0, 0, 0, 0.18);
    cursor: pointer;
    transition: background-color .2s ease;

    &:hover { background: #F5F6FF; }
    &:focus-visible {
        outline: 2px solid #FFFFFF;
        outline-offset: 2px;
    }
`
const Quote = styled.blockquote`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin: 0;
    padding-left: 14px;
    border-left: 3px solid #6B57E3;
`
const QuoteText = styled.p`
    margin: 0;
    font-size: 13px;
    font-style: italic;
    line-height: 19px;
    color: rgba(255, 255, 255, 0.8);
`
const QuoteAuthor = styled.cite`
    font-size: 11px;
    font-style: normal;
    line-height: 16px;
    color: rgba(255, 255, 255, 0.6);
`
const FeatureGrid = styled.ul`
    display: grid;
    gap: 14px;
    margin: 0;
    padding: 0;
    list-style: none;

    @media only screen and (min-width: 640px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`
const Feature = styled.li`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 14px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.03));
    box-shadow: 0 6px 10px 0 rgba(0, 0, 0, 0.28);
`
const FeatureHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`
const IconBox = styled.span`
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 30px;
    height: 30px;
    border-radius: 9px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(97, 85, 236, 0.3);
    box-shadow: inset 0 0 0 1px rgba(81, 76, 227, 0.3);
    color: #FFFFFF;
`
const FeatureTitle = styled.h3`
    margin: 0;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    color: #FFFFFF;
`
const FeatureDescription = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 17px;
    color: rgba(255, 255, 255, 0.7);
`

export default EnterpriseSection
